import { BorderBox } from "@/components/atoms/BorderBox";
import { DynamicHeightScrollArea } from "@/components/atoms/DynamicHeightScrollArea";
import { MarkSearchedPhrase } from "@/components/atoms/MarkSearchedPhrase";
import { SearchInput } from "@/components/molecules/SearchInput";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Sidebar, SidebarContent, SidebarHeader, useSidebar } from "@/components/ui/sidebar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ENV } from "@/constants/env";
import { SIDEBAR_NAMES } from "@/constants/sidebar";
import { useGetOrganizations } from "@/features/organization/hooks/useQuery/useGetOrganizations";
import { Autocomplete } from "@/types/utils";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { HELP_SIDEBAR_TUTORIALS } from "../../constants/tutorials";
import { ScribeHowTutorial } from "../../types/tutorials";
import { TutorialDialog } from "./TutorialDialog/TutorialDialog";



export function HelpSidebar() {
    const { toggleSidebar } = useSidebar();

    const [selectedTutorial, setSelectedTutorial] = useState<ScribeHowTutorial | null>(null);
    const [search, setSearch] = useState<string>('');
    const [openSections, setOpenSections] = useState<string[]>([]);

    const filteredSections = useMemo(() => {
        return HELP_SIDEBAR_TUTORIALS.map(section => {
            const filteredTutorials = section.tutorials.filter(tutorial => {
                const title = tutorial.title.toLowerCase();
                const description = tutorial.description.toLowerCase();
                const searchValue = search?.toLowerCase() || '';
                return title.includes(searchValue) || description.includes(searchValue);
            });
            return {
                ...section,
                tutorials: filteredTutorials
            }
        }).filter(section => section.tutorials.length > 0);
    }, [search]);

    useEffect(() => {
        if (search) {
            setOpenSections(filteredSections.map(section => section.title));
        }
    }, [search, filteredSections]);

    const [currentTab, setCurrentTab] = useState<Autocomplete<'tutorials' | 'faqs' | 'downloadable-materials'>>('tutorials');
    return (
        <Sidebar
            name={SIDEBAR_NAMES.HELP_SIDEBAR}
            side="right"
        >
            <TutorialDialog
                title={selectedTutorial?.title || ''}
                description={selectedTutorial?.description || ''}
                scribeHowEmbedLink={selectedTutorial?.scribeHowEmbedLink || ''}
                open={!!selectedTutorial}
                onOpenChange={() => setSelectedTutorial(null)}
            />
            <SidebarContent
                className="p-2 border-l-2 border-l-lighter-gray"
            >

                <Tabs defaultValue="tutorials" className="flex flex-col h-full" value={currentTab} onValueChange={setCurrentTab}>
                    <SidebarHeader className="p-0">
                        <div className="h-navbar"></div>
                        <X onClick={() => toggleSidebar([SIDEBAR_NAMES.HELP_SIDEBAR])} size={14} className="ml-auto" />
                        <TabsList className="grid grid-cols-3">
                            <TabsTrigger value="tutorials" className="text-xs">Tutorials {search.length > 0 ? `(${filteredSections.length})` : ''}</TabsTrigger>
                            <TabsTrigger value="downloadable-materials" className="text-xs">Downloadable</TabsTrigger>
                            <TabsTrigger value="faqs" className="text-xs">FAQs</TabsTrigger>
                        </TabsList>
                        {currentTab === "tutorials" && <SearchInput value={search} setValue={setSearch} />}
                    </SidebarHeader>
                    <DynamicHeightScrollArea offset={8}>
                        <TabsContent value="tutorials">
                            <Accordion
                                type="multiple"
                                className="flex flex-col items-stretch"
                                value={openSections}
                                onValueChange={setOpenSections}
                            >
                                {filteredSections.map((section) => (
                                    <AccordionItem key={section.title} value={section.title}>
                                        <AccordionTrigger>{section.title}</AccordionTrigger>
                                        <AccordionContent className="space-y-2">
                                            <p>
                                                {section.description}
                                            </p>
                                            <hr />
                                            {section.tutorials.map((tutorial, index) => (
                                                <BorderBox
                                                    className="flex-col items-start gap-3 p-2"
                                                    key={tutorial.title}
                                                >
                                                    <Button
                                                        onClick={() => setSelectedTutorial(tutorial)}
                                                        variant="link"
                                                        className="items-start justify-start w-full h-auto gap-1 p-0 space-x-1 text-left whitespace-normal text-wrap"
                                                    >
                                                        {index + 1}{". "}
                                                        <MarkSearchedPhrase searchTerm={search} text={tutorial.title} />
                                                    </Button>
                                                    <p className="text-sm">
                                                        <MarkSearchedPhrase searchTerm={search} text={tutorial.description} />
                                                    </p>
                                                </BorderBox>
                                            ))}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </TabsContent>
                        <TabsContent value="faqs">

                        </TabsContent>
                        <TabsContent value="downloadable-materials">
                            <DownloadableMaterialsTab />
                        </TabsContent>
                    </DynamicHeightScrollArea>
                </Tabs>
            </SidebarContent>
        </Sidebar>
    )
}

function DownloadableMaterialsTab() {
    return (
        <div className="flex flex-col">
            <SolveSpecificDownloadableMaterials />
        </div>
    )
}

function SolveSpecificDownloadableMaterials() {
    const { data: organizationsList } = useGetOrganizations({ isEnabled: true });
    return (
        <div className="flex flex-col">
            {organizationsList?.find(org => org.id === ENV.VITE_SOLVE_ORG_ID) && (
                <div className="flex flex-col gap-2">
                    <BorderBox variant={'info'} size='sm'>
                        Materials for Solve members (only visible to Solve organization members)
                    </BorderBox>
                    <SolveExampleCrmImportDownloadableMaterialCard />
                    <Separator />
                </div>
            )}
        </div>
    )
}

interface DownloadableMaterialCardProps {
    title: string;
    description: string;
    downloadLink: string;
}

function DownloadableMaterialCard({ title, description, downloadLink }: Readonly<DownloadableMaterialCardProps>) {
    return (
        <BorderBox className="flex flex-col items-start gap-2">
            <h3 className="text-sm font-medium">{title}</h3>
            <p className="text-sm">{description}</p>
            <Button asChild size='sm' className="w-full">
                <a href={downloadLink} download>Download</a>
            </Button>
        </BorderBox>
    )
}

function SolveExampleCrmImportDownloadableMaterialCard() {
    return (
        <DownloadableMaterialCard
            title="Solve Example CRM Import File"
            description="An example CSV file demonstrating the correct format for importing CRM data into Solve."
            downloadLink="/downloadable/CRM_Route_Dependency_IDs_example.csv"
        />
    )
}